import { alpha, lighten, darken } from '@mui/material';

export const themeColors = {
  primary: '#5569ff',
  secondary: '#6E759F',
  success: '#57CA22',
  warning: '#FFA319',
  error: '#FF1943',
  info: '#33C2FF',
  black: '#223354',
  white: '#ffffff',
  primaryAlt: '#000C57',
  customPrimary: '#001d3b',
//   customPrimary: '#021b39',
  customSecondary: '#ff5d1a'
};

// Generate color variants
export const generateColorVariants = (color) => ({
  lighter: alpha(color, 0.1),
  light: lighten(color, 0.3),
  main: color,
  dark: darken(color, 0.2),
  contrastText: themeColors.white
});

// Generate palette for light mode
export const lightPalette = {
  mode: 'light',
  common: {
    black: themeColors.black,
    white: themeColors.white
  },
  primary: generateColorVariants(themeColors.primary),
  secondary: generateColorVariants(themeColors.secondary),
  success: generateColorVariants(themeColors.success),
  warning: generateColorVariants(themeColors.warning),
  error: generateColorVariants(themeColors.error),
  info: generateColorVariants(themeColors.info),
  customPrimary: generateColorVariants(themeColors.customPrimary),
  customSecondary: generateColorVariants(themeColors.customSecondary),
  text: {
    primary: themeColors.black,
    secondary: alpha(themeColors.black, 0.7),
    disabled: alpha(themeColors.black, 0.5)
  },
  background: {
    paper: themeColors.white,
    default: '#f2f5f9',
    main: '#f2f5f9',
  },
  action: {
    active: alpha(themeColors.black, 0.54),
    hover: alpha(themeColors.black, 0.04),
    hoverOpacity: 0.04,
    selected: alpha(themeColors.black, 0.08),
    selectedOpacity: 0.08,
    disabled: alpha(themeColors.black, 0.26),
    disabledBackground: alpha(themeColors.black, 0.12),
    disabledOpacity: 0.38,
    focus: alpha(themeColors.black, 0.12),
    focusOpacity: 0.12,
    activatedOpacity: 0.12
  },
  divider: alpha(themeColors.black, 0.12)
};

// Generate palette for dark mode
export const darkPalette = {
  mode: 'dark',
  common: {
    black: '#000000',
    white: '#ffffff'
  },
  primary: {
    main: '#5569ff',
    light: '#8589ff',
    dark: '#004ecb',
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#6E759F',
    light: '#9ea4cc',
    dark: '#404a70',
    contrastText: '#ffffff'
  },
  success: generateColorVariants(themeColors.success),
  warning: generateColorVariants(themeColors.warning),
  error: generateColorVariants(themeColors.error),
  info: generateColorVariants(themeColors.info),
  customPrimary: generateColorVariants(themeColors.customPrimary),
  customSecondary: generateColorVariants(themeColors.customSecondary),
  text: {
    primary: '#ffffff',
    secondary: alpha('#ffffff', 0.7),
    disabled: alpha('#ffffff', 0.5)
  },
  background: {
      default: '#021b39',
      paper: '#0f2743',
      main: '#001d3b',
    // paper: '#091929',
    // default: '#091929'
  },
  action: {
    active: '#ffffff',
    hover: alpha('#ffffff', 0.08),
    hoverOpacity: 0.08,
    selected: alpha('#ffffff', 0.16),
    selectedOpacity: 0.16,
    disabled: alpha('#ffffff', 0.3),
    disabledBackground: alpha('#ffffff', 0.12),
    disabledOpacity: 0.38,
    focus: alpha('#ffffff', 0.12),
    focusOpacity: 0.12,
    activatedOpacity: 0.24
  },
  divider: alpha('#ffffff', 0.12)
};

// Shared gradients
export const gradients = {
  blue1: 'linear-gradient(135deg, #6B73FF 0%, #000DFF 100%)',
  blue2: 'linear-gradient(135deg, #ABDCFF 0%, #0396FF 100%)',
  blue3: 'linear-gradient(127.55deg, #141E30 3.73%, #243B55 92.26%)',
  blue4: 'linear-gradient(-20deg, #2b5876 0%, #4e4376 100%)',
  blue5: 'linear-gradient(135deg, #97ABFF 10%, #123597 100%)',
  orange1: 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)',
  orange2: 'linear-gradient(135deg, #FFD3A5 0%, #FD6585 100%)',
  orange3: 'linear-gradient(120deg, #f6d365 0%, #fda085 100%)',
  purple1: 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)',
  purple3: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  pink1: 'linear-gradient(135deg, #F6CEEC 0%, #D939CD 100%)',
  pink2: 'linear-gradient(135deg, #F761A1 0%, #8C1BAB 100%)',
  green1: 'linear-gradient(135deg, #FFF720 0%, #3CD500 100%)',
  green2: 'linear-gradient(to bottom, #00b09b, #96c93d)',
  black1: 'linear-gradient(100.66deg, #434343 6.56%, #000000 93.57%)',
  black2: 'linear-gradient(60deg, #29323c 0%, #485563 100%)'
};

// Shared shadows
export const shadows = {
  success: '0px 1px 4px rgba(68, 214, 0, 0.25), 0px 3px 12px 2px rgba(68, 214, 0, 0.35)',
  error: '0px 1px 4px rgba(255, 25, 67, 0.25), 0px 3px 12px 2px rgba(255, 25, 67, 0.35)',
  info: '0px 1px 4px rgba(51, 194, 255, 0.25), 0px 3px 12px 2px rgba(51, 194, 255, 0.35)',
  primary: '0px 1px 4px rgba(85, 105, 255, 0.25), 0px 3px 12px 2px rgba(85, 105, 255, 0.35)',
  warning: '0px 1px 4px rgba(255, 163, 25, 0.25), 0px 3px 12px 2px rgba(255, 163, 25, 0.35)',
  card: '0px 9px 16px rgba(159, 162, 191, .18), 0px 2px 2px rgba(159, 162, 191, 0.32)',
  cardSm: '0px 2px 3px rgba(159, 162, 191, .18), 0px 1px 1px rgba(159, 162, 191, 0.32)',
  cardLg: '0 5rem 14rem 0 rgb(255 255 255 / 30%), 0 0.8rem 2.3rem rgb(0 0 0 / 60%), 0 0.2rem 0.3rem rgb(0 0 0 / 45%)'
};

// Common components config (shared between themes)
export const commonComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      'html, body': {
        width: '100%',
        height: '100%'
      },
      body: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        flex: 1
      },
      '#root': {
        width: '100%',
        height: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column'
      },
      html: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased'
      },
      '.child-popover .MuiPaper-root .MuiList-root': {
        flexDirection: 'column'
      },
      '#nprogress': {
        pointerEvents: 'none'
      },
      '#nprogress .bar': {
        background: lighten(themeColors.primary, 0.85)
      },
      '#nprogress .spinner-icon': {
        borderTopColor: lighten(themeColors.primary, 0.85),
        borderLeftColor: lighten(themeColors.primary, 0.85)
      },
      '#nprogress .peg': {
        boxShadow: `0 0 15px ${lighten(themeColors.primary, 0.85)}, 0 0 8px ${lighten(themeColors.primary, 0.3)}`
      },
      ':root': {
        '--swiper-theme-color': themeColors.primary
      },
      code: {
        background: lighten(themeColors.info, 0.85),
        color: darken(themeColors.info, 0.2),
        borderRadius: 4,
        padding: 4
      }
    }
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true
    },
    styleOverrides: {
      root: {
        fontWeight: 'bold',
        textTransform: 'none',
        borderRadius: 8,
        '.MuiSvgIcon-root': {
          transition: 'all .2s'
        }
      },
      endIcon: {
        marginRight: -8
      },
      sizeSmall: {
        padding: '6px 16px',
        lineHeight: 1.5
      },
      sizeMedium: {
        padding: '8px 20px'
      },
      sizeLarge: {
        padding: '11px 24px'
      },
      textSizeSmall: {
        padding: '7px 12px'
      },
      textSizeMedium: {
        padding: '9px 16px'
      },
      textSizeLarge: {
        padding: '12px 16px'
      }
    }
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        h1: 'h1',
        h2: 'h2',
        h3: 'div',
        h4: 'div',
        h5: 'div',
        h6: 'div',
        subtitle1: 'div',
        subtitle2: 'div',
        body1: 'div',
        body2: 'div'
      }
    },
    styleOverrides: {
      root: {
        color: 'inherit'
      },
      gutterBottom: {
        marginBottom: 4
      },
      paragraph: {
        fontSize: 17,
        lineHeight: 1.7
      }
    }
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: '4px 8px',
        '& .MuiTouchRipple-root': {
          opacity: 0.3
        }
      }
    }
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        border: 0,
        height: 1
      },
      vertical: {
        height: 'auto',
        width: 1
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        padding: 0
      }
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      action: {
        marginTop: -5,
        marginBottom: -5
      },
      title: {
        fontSize: 15
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 24
      }
    }
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(darken(themeColors.primaryAlt, 0.4), 0.2),
        backdropFilter: 'blur(2px)'
      }
    }
  },
  MuiSelect: {
    styleOverrides: {
      icon: {
        top: 'calc(50% - 14px)'
      }
    }
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontSize: 14,
        fontWeight: 'bold'
      }
    }
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        height: 6
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        padding: 12
      },
      list: {
        padding: 12
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: 14
      },
      head: {
        textTransform: 'uppercase',
        fontSize: 13,
        fontWeight: 'bold'
      }
    }
  }
};